import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';
import { API_BASE } from '../api/offlineClient';
import connectivity from '../utils/connectivity';
import syncService from '../services/syncService';  // instancia el servicio de sync
import OfflineIndicator from './OfflineIndicator';
import BrandMark from './BrandMark';

const NAV_BASE = [
  { to: '/',               icon: '⊞',  label: 'Inicio' },
  { to: '/calendario',    icon: '📅', label: 'Calendario' },
  { to: '/instalaciones',  icon: '📍', label: 'Instalaciones' },
  { to: '/actas',          icon: '📋', label: 'Actas' },
  { to: '/prejob-segavan', icon: '📝', label: 'Prejob Segavan' },
  { to: '/gastos',         icon: '💶', label: 'Nota de Gastos' },
  { to: '/partes',         icon: '📄', label: 'Partes' },
  { to: '/endesa',         icon: '⚡', label: 'ENDESA' },
];
const NAV_ADMIN = [
  { to: '/',               icon: '⊞',  label: 'Inicio' },
  { to: '/fichajes',       icon: '⏱️', label: 'Fichajes' },
  { to: '/calendario',     icon: '📅', label: 'Calendario' },
  { to: '/vacaciones',     icon: '🏖️', label: 'Vacaciones' },
  { to: '/planificacion',  icon: '📆', label: 'Planificación' },
  { to: '/instalaciones',  icon: '📍', label: 'Instalaciones' },
  { to: '/empleados',      icon: '👥', label: 'Empleados' },
  { to: '/gastos',         icon: '💶', label: 'Nota de Gastos' },
  { to: '/actas',          icon: '📋', label: 'Actas' },
  { to: '/prejob-segavan', icon: '📝', label: 'Prejob Segavan' },
  { to: '/partes',         icon: '📄', label: 'Partes' },
  { to: '/inventario',     icon: '🏭', label: 'Inventario' },
  { to: '/admin-docs',     icon: '📤', label: 'Subir Docs' },
  { to: '/endesa',         icon: '⚡', label: 'ENDESA' },
  { to: '/coches',         icon: '🚗', label: 'Coches' },
];

// Agrupación visual del resto de items (Inicio va siempre fijo arriba, fuera
// de estas categorías). Un item que no aparezca en ninguna lista se muestra
// suelto, sin cabecera, antes de las categorías (Instalaciones, Inventario, Coches).
const NAV_CATEGORIES = [
  { key: 'equipo',        label: 'Equipo',        routes: ['/fichajes', '/calendario', '/vacaciones', '/planificacion', '/empleados'] },
  { key: 'documentacion', label: 'Documentación', routes: ['/actas', '/prejob-segavan', '/partes', '/endesa', '/admin-docs', '/gastos'] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed,    setCollapsed]    = useState(false);
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [isMobile,     setIsMobile]     = useState(window.innerWidth < 640);
  const [nominasCount,    setNominasCount]    = useState(0);
  const [pendientesAdmin, setPendientesAdmin] = useState(0);
  const [vacacionesNuevas, setVacacionesNuevas] = useState(0);
  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') !== 'light');
  const [navSearch, setNavSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const navRef = useRef(null);
  const [indicator, setIndicator] = useState({ top: 0, height: 0, visible: false });
  const [closedCats, setClosedCats] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sgv_nav_closed_cats') || '{}'); } catch { return {}; }
  });

  function toggleCat(key) {
    setClosedCats(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('sgv_nav_closed_cats', JSON.stringify(next)); } catch {}
      return next;
    });
  }

  const isAdmin = ['admin','jefe','administrativo'].includes(user?.rol);

  // Indicador de navegación: mide la posición del item activo y la barra
  // lateral (.sg-nav-indicator) viaja hasta ahí con una animación tipo "muelle".
  useLayoutEffect(() => {
    const navEl = navRef.current;
    const activeEl = navEl?.querySelector('.sg-nav-item.active');
    if (navEl && activeEl) {
      const navRect = navEl.getBoundingClientRect();
      const elRect = activeEl.getBoundingClientRect();
      setIndicator({ top: elRect.top - navRect.top + elRect.height * 0.18, height: elRect.height * 0.64, visible: true });
    } else {
      setIndicator(i => ({ ...i, visible: false }));
    }
  }, [location.pathname, collapsed, isMobile, drawerOpen, closedCats]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Inicializar connectivity con la URL base para el heartbeat
  useEffect(() => {
    connectivity.setApiBase(API_BASE);
    // Arrancar syncService (el import ya lo instancia; esto asegura que escuche)
    syncService.syncNow().catch(() => {});
    // Escuchar mensajes del Service Worker (background sync)
    if ('serviceWorker' in navigator) {
      const handler = e => {
        if (e.data?.type === 'sgv:synced') {
          window.dispatchEvent(new CustomEvent('sgv:synced', { detail: e.data }));
        }
      };
      navigator.serviceWorker.addEventListener('message', handler);
      return () => navigator.serviceWorker.removeEventListener('message', handler);
    }
  }, []); // eslint-disable-line

  // Badge nóminas no leídas (solo técnicos)
  useEffect(() => {
    if (isAdmin || !user) return;
    api.get('/api/nominas/nuevas')
      .then(r => setNominasCount(r.count || 0))
      .catch(() => {});
  }, [location.pathname, isAdmin, user]);

  // Badge pendientes admin (vacaciones + reembolsos sin revisar)
  useEffect(() => {
    if (!isAdmin || !user) return;
    const fetch = () => api.get('/api/calendar/pendientes')
      .then(r => setPendientesAdmin((r.vacaciones || 0) + (r.facturas || 0)))
      .catch(() => {});
    fetch();
    window.addEventListener('sgv:pendientes', fetch);
    return () => window.removeEventListener('sgv:pendientes', fetch);
  }, [location.pathname, isAdmin, user]); // eslint-disable-line

  // Badge vacaciones/bajas resueltas sin ver (solo técnicos)
  useEffect(() => {
    if (isAdmin || !user) return;
    const fetch = () => api.get('/api/calendar/vacaciones/nuevas')
      .then(r => setVacacionesNuevas(r.count || 0))
      .catch(() => {});
    fetch();
    window.addEventListener('sgv:pendientes', fetch);
    return () => window.removeEventListener('sgv:pendientes', fetch);
  }, [location.pathname, isAdmin, user]); // eslint-disable-line

  // Cerrar drawer al navegar
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Aplicar tema
  useEffect(() => {
    const t = isDark ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', t);
    localStorage.setItem('theme', t);
    document.querySelectorAll('iframe').forEach(f => {
      try { f.contentWindow.postMessage({ type: 'sgv-theme', value: t }, '*'); } catch (_) {}
    });
  }, [isDark]);

  function handleLogout() { logout(); navigate('/login'); }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)', overflowX: 'hidden', maxWidth: '100vw' }}>

      {/* ── Backdrop móvil ── */}
      {isMobile && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
            zIndex: 299, backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* ── SIDEBAR ── */}
      <aside style={{
        width: isMobile ? 260 : (collapsed ? 56 : 220),
        background: `radial-gradient(420px 220px at 0% 0%, color-mix(in srgb, var(--red) 8%, transparent) 0%, transparent 60%), var(--surface2)`,
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        flexShrink: 0,
        position: isMobile ? 'fixed' : 'sticky',
        top: 0, left: 0,
        height: '100vh',
        overflowY: 'auto',
        zIndex: isMobile ? 300 : 1,
        transition: isMobile
          ? 'transform .25s cubic-bezier(.4,0,.2,1)'
          : 'width .2s',
        transform: isMobile
          ? (drawerOpen ? 'translateX(0)' : 'translateX(-100%)')
          : 'none',
      }}>

        {/* Logo + botón colapsar/cerrar */}
        <div style={{
          padding: isMobile ? 'calc(16px + env(safe-area-inset-top)) 18px 16px' : (collapsed ? '18px 0' : '18px 16px'),
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center',
          justifyContent: (!isMobile && collapsed) ? 'center' : 'space-between',
          gap: 10,
        }}>
          {(() => {
            const mark = <BrandMark size={28} />;
            if (!isMobile && collapsed) return mark;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, overflow: 'hidden' }}>
                {mark}
                <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ color: 'var(--red)' }}>Seg</span><span style={{ color: 'var(--text)' }}>Avan</span>
                </div>
              </div>
            );
          })()}
          <button
            onClick={() => isMobile ? setDrawerOpen(false) : setCollapsed(c => !c)}
            className="sg-btn-ghost"
            style={{
              fontSize: isMobile ? 20 : 16, padding: 4, lineHeight: 1, borderRadius: 6,
            }}
          >
            <span style={{ display: 'inline-block', transition: 'transform .2s var(--ease)', transform: collapsed ? 'rotate(180deg)' : 'none' }}>
              {isMobile ? '✕' : '‹'}
            </span>
          </button>
        </div>

        {/* Nav items */}
        <nav ref={navRef} style={{ flex: 1, padding: '8px 0', position: 'relative' }}>
          <div className="sg-nav-indicator" style={{
            top: indicator.top, height: indicator.height,
            opacity: indicator.visible ? 1 : 0,
          }} />
          {(() => {
            const allItems = (['admin','jefe','administrativo'].includes(user?.rol) ? NAV_ADMIN : NAV_BASE)
              .filter(item => item.to !== '/prejob-segavan' || user?.rol === 'tecnico');
            const [inicioItem, ...restItems] = allItems;
            const q = navSearch.trim().toLowerCase();
            const filteredRest = q ? restItems.filter(i => i.label.toLowerCase().includes(q)) : restItems;

            const catRoutes = new Set(NAV_CATEGORIES.flatMap(c => c.routes));
            const grouped = NAV_CATEGORIES
              .map(cat => ({ ...cat, items: restItems.filter(i => cat.routes.includes(i.to)) }))
              .filter(cat => cat.items.length > 0);
            const uncategorized = restItems.filter(i => !catRoutes.has(i.to));

            const renderItem = ({ to, icon, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) => `sg-nav-item${isActive ? ' active' : ''}`}
                style={{
                  display: 'flex', alignItems: 'center',
                  gap: isMobile ? 14 : (collapsed ? 0 : 10),
                  padding: isMobile ? '14px 18px' : (collapsed ? '11px 0' : '11px 16px'),
                  justifyContent: (!isMobile && collapsed) ? 'center' : 'flex-start',
                  textDecoration: 'none',
                  fontSize: isMobile ? 15 : 13, fontWeight: 600,
                  minHeight: isMobile ? 52 : 'auto',
                }}
              >
                <span style={{ fontSize: isMobile ? 20 : 16, flexShrink: 0, position: 'relative' }}>
                  {icon}
                  {to === '/calendario' && pendientesAdmin > 0 && (
                    <span style={{ position: 'absolute', top: -4, right: -6, background: 'var(--bad)',
                      color: '#fff', borderRadius: '50%', fontSize: 9, fontWeight: 900,
                      minWidth: 14, height: 14, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', lineHeight: 1, padding: '0 2px' }}>
                      {pendientesAdmin}
                    </span>
                  )}
                  {to === '/calendario' && !isAdmin && vacacionesNuevas > 0 && (
                    <span style={{ position: 'absolute', top: -4, right: -6, background: 'var(--bad)',
                      color: '#fff', borderRadius: '50%', fontSize: 9, fontWeight: 900,
                      minWidth: 14, height: 14, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', lineHeight: 1, padding: '0 2px' }}>
                      {vacacionesNuevas}
                    </span>
                  )}
                </span>
                {(isMobile || !collapsed) && <span>{label}</span>}
              </NavLink>
            );

            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>{inicioItem && renderItem(inicioItem)}</div>
                  {isAdmin && (isMobile || !collapsed) && (
                    <button
                      onClick={() => { setSearchOpen(o => { const next = !o; if (!next) setNavSearch(''); return next; }); }}
                      title="Buscar sección"
                      style={{
                        background: searchOpen ? 'var(--surface)' : 'none', border: 'none',
                        color: searchOpen ? 'var(--amber)' : 'var(--text-dim)', cursor: 'pointer',
                        padding: '8px 14px 8px 6px', fontSize: 15, flexShrink: 0, lineHeight: 1,
                      }}
                    >🔍</button>
                  )}
                </div>

                {isAdmin && searchOpen && (isMobile || !collapsed) && (
                  <div style={{ padding: '2px 14px 8px' }}>
                    <input
                      autoFocus
                      type="text"
                      value={navSearch}
                      onChange={e => setNavSearch(e.target.value)}
                      placeholder="Buscar sección…"
                      style={{
                        width: '100%', background: 'var(--surface-deep)', border: '1px solid var(--border)',
                        borderRadius: 6, color: 'var(--text)', padding: '6px 9px', fontSize: 12,
                        fontFamily: 'inherit', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                )}

                {!isAdmin ? (
                  <>{restItems.map(renderItem)}</>
                ) : q ? (
                  filteredRest.length === 0
                    ? (isMobile || !collapsed) && (
                        <div style={{ padding: '4px 18px', fontSize: 11, color: 'var(--text-dim)' }}>Sin resultados</div>
                      )
                    : filteredRest.map(renderItem)
                ) : (isMobile || !collapsed) ? (
                  <>
                    {uncategorized.map(renderItem)}
                    {grouped.map(cat => {
                      const isOpen = !closedCats[cat.key];
                      return (
                        <div key={cat.key}>
                          <button
                            onClick={() => toggleCat(cat.key)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                              padding: isMobile ? '10px 18px 6px' : '10px 16px 5px',
                              fontSize: 10, fontWeight: 800, color: 'var(--text-dim)',
                              textTransform: 'uppercase', letterSpacing: '.08em',
                            }}
                          >
                            <span>{cat.label}</span>
                            <span style={{ display: 'inline-block', transition: 'transform .15s var(--ease)', transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize: 9 }}>▾</span>
                          </button>
                          {isOpen && cat.items.map(renderItem)}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>{restItems.map(renderItem)}</>
                )}
              </>
            );
          })()}
        </nav>

        {/* User / logout */}
        <div style={{
          padding: isMobile ? '14px 18px' : (collapsed ? '12px 0' : '12px 14px'),
          borderTop: '1px solid var(--border)',
        }}>
          {(isMobile || !collapsed) ? (
            <div
              onClick={() => navigate('/perfil')}
              title="Mi Perfil"
              style={{
                marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10,
                cursor: 'pointer', borderRadius: 8, padding: '4px 6px', margin: '-4px -6px 8px',
                transition: 'background .15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div className="sg-tile" style={{
                '--tile': 'var(--amber)', width: 32, height: 32, borderRadius: '50%',
                fontSize: 13, fontWeight: 900, flexShrink: 0, overflow: 'hidden', position: 'relative',
              }}>
                {user?.foto_url
                  ? <img src={user.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : (user?.nombre || '?').charAt(0).toUpperCase()}
                {nominasCount > 0 && (
                  <span style={{ position: 'absolute', top: -3, right: -3, background: 'var(--bad)',
                    color: '#fff', borderRadius: '50%', fontSize: 9, fontWeight: 900,
                    minWidth: 14, height: 14, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', lineHeight: 1, padding: '0 2px', border: '1.5px solid var(--surface2)' }}>
                    {nominasCount}
                  </span>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: isMobile ? 14 : 12, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.nombre}
                </div>
                <span className={`sg-badge ${user?.rol === 'admin' ? 'sg-badge-admin' : ['jefe','administrativo'].includes(user?.rol) ? 'sg-badge-jefe' : 'sg-badge-tecnico'}`} style={{ marginTop: 3 }}>
                  {user?.rol?.toUpperCase()}
                </span>
              </div>
            </div>
          ) : (
            <div
              onClick={() => navigate('/perfil')}
              title="Mi Perfil"
              style={{ marginBottom: 10, display: 'flex', justifyContent: 'center', cursor: 'pointer' }}
            >
              <div className="sg-tile" style={{
                '--tile': 'var(--amber)', width: 30, height: 30, borderRadius: '50%',
                fontSize: 12, fontWeight: 900, flexShrink: 0, overflow: 'hidden', position: 'relative',
              }}>
                {user?.foto_url
                  ? <img src={user.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : (user?.nombre || '?').charAt(0).toUpperCase()}
                {nominasCount > 0 && (
                  <span style={{ position: 'absolute', top: -3, right: -3, background: 'var(--bad)',
                    color: '#fff', borderRadius: '50%', fontSize: 8, fontWeight: 900,
                    minWidth: 12, height: 12, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', lineHeight: 1, padding: '0 2px', border: '1.5px solid var(--surface2)' }}>
                    {nominasCount}
                  </span>
                )}
              </div>
            </div>
          )}
          <button
            onClick={() => setIsDark(d => !d)}
            title={isDark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              color: 'var(--text-dim)', borderRadius: 6, cursor: 'pointer',
              padding: isMobile ? '10px 14px' : (collapsed ? '6px 8px' : '6px 12px'),
              fontSize: isMobile ? 13 : 11, fontWeight: 700, width: '100%',
              marginBottom: 6, transition: 'all .15s',
              display: 'flex', alignItems: 'center',
              justifyContent: (!isMobile && collapsed) ? 'center' : 'flex-start', gap: 7,
            }}
          >
            <span style={{ fontSize: 14 }}>{isDark ? '☀' : '☾'}</span>
            {(isMobile || !collapsed) && <span>{isDark ? 'Tema claro' : 'Tema oscuro'}</span>}
          </button>
          <button
            onClick={handleLogout}
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              color: 'var(--text-dim)', borderRadius: 6, cursor: 'pointer',
              padding: isMobile ? '10px 14px' : (collapsed ? '6px 8px' : '6px 12px'),
              fontSize: isMobile ? 13 : 11, fontWeight: 700, width: '100%',
              transition: 'all .15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--bad)'; e.currentTarget.style.color = 'var(--bad)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-dim)'; }}
          >
            {(!isMobile && collapsed) ? '→' : '⎋ Salir'}
          </button>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100vh' }}>

        {/* Barra superior móvil con hamburguesa */}
        {isMobile && (
          <div style={{
            background: 'var(--surface2)', borderBottom: '1px solid var(--border)',
            padding: 'calc(10px + env(safe-area-inset-top)) 14px 10px', display: 'flex', alignItems: 'center', gap: 12,
            flexShrink: 0,
          }}>
            <button
              onClick={() => setDrawerOpen(true)}
              style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                color: 'var(--text)', cursor: 'pointer', padding: '8px 12px',
                borderRadius: 7, fontSize: 17, lineHeight: 1, fontWeight: 700,
                flexShrink: 0,
              }}
            >
              ☰
            </button>
            {!drawerOpen && (
              <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: -.5 }}>
                <span style={{ color: 'var(--red)' }}>Seg</span><span style={{ color: 'var(--text)' }}>Avan</span>
              </div>
            )}
          </div>
        )}

        <OfflineIndicator />

        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
          {children}
        </div>
      </main>
    </div>
  );
}
